/**
 * Neutral loading placeholder for auth screens.
 *
 * Shown:
 * - While a route's data dependencies (getSession / listPasskeys / etc.)
 *   are still loading.
 * - During the brief window between "data loaded" and "navigation effect
 *   fires" — without this, the route's actual UI renders for a split
 *   second before the navigation kicks in, which the user perceives as
 *   a flicker (AUTH-136).
 *
 * Renders a skeleton that matches the auth-card vertical rhythm so the
 * card doesn't jump in size when content swaps in. Two skeleton blocks
 * mirror the typical "title + subtitle, then a primary action / form."
 */
import { Loader2 } from "lucide-react";

export function AuthLoading({
  label = "Loading…",
  "data-testid": testId,
}: {
  label?: string;
  /**
   * Caller-supplied — seven screens render this, two of them twice, so a
   * hard-coded id would resolve to whichever the DOM ordered first. This is
   * the element a test waits out before asserting anything on an auth screen.
   */
  "data-testid"?: string;
} = {}) {
  return (
    <div
      className="space-y-6"
      role="status"
      aria-live="polite"
      aria-label={label}
      data-testid={testId}
    >
      <div className="space-y-2">
        <div className="mx-auto h-7 w-48 animate-pulse rounded bg-muted" />
        <div className="mx-auto h-4 w-64 animate-pulse rounded bg-muted" />
      </div>
      <div className="space-y-3">
        <div className="h-10 w-full animate-pulse rounded bg-muted" />
        <div className="h-10 w-full animate-pulse rounded bg-muted/60" />
      </div>
      <div className="flex justify-center pt-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    </div>
  );
}
